import {
  AfterViewInit,
  DestroyRef,
  Directive,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormGroupDirective,
  NgControl,
} from '@angular/forms';
import { merge, tap } from 'rxjs';
import { VALIDATION_ERRORS } from './validation-errors.const';

@Directive({})
export abstract class BaseCVAComponent<T>
  implements ControlValueAccessor, AfterViewInit
{
  private readonly injector = inject(Injector);
  protected readonly destroyRef = inject(DestroyRef);

  private readonly formGroup = inject(FormGroupDirective, { optional: true });

  protected readonly value = signal<T | null>(null);
  protected readonly disabled = signal(false);
  protected readonly error = signal('');

  public ngAfterViewInit(): void {
    this.listenControlState();
  }

  private updateValidationState(): void {
    const control = this.control;

    if (!control) {
      return;
    }

    const shouldShow =
      control.invalid &&
      (control.touched ||
        control.dirty ||
        (this.formGroup?.submitted ?? false));

    if (!shouldShow) {
      this.error.set('');
      return;
    }

    const errors = control.errors;

    if (!errors) {
      return;
    }

    const errorKey = Object.keys(errors)[0];

    const message = VALIDATION_ERRORS[errorKey];

    this.error.set(
      typeof message === 'string'
        ? message
        : (message?.(errors[errorKey]) ?? 'Invalid field.'),
    );
  }

  private listenControlState(): void {
    const control = this.control;

    if (!control) {
      return;
    }

    merge(control.statusChanges, control.valueChanges, control.events)
      .pipe(
        tap(() => this.updateValidationState()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected onChange: (value: T | null) => void = () => {};
  protected onTouched: () => void = () => {};

  public writeValue(value: T | null): void {
    this.value.set(value);
  }

  public registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected updateValue(value: T | null): void {
    this.value.set(value);
    this.onChange(value);
  }

  protected markAsTouched(): void {
    this.onTouched();
  }

  protected get ngControl(): NgControl | null {
    return this.injector.get(NgControl, null, {
      self: true,
      optional: true,
    });
  }

  protected get control() {
    return this.ngControl?.control;
  }
}
